<script lang="ts">
    import { onDestroy, onMount } from "svelte";
    import { LL } from "../../../i18n/i18n-svelte";
    import logoImg from "../images/logo-cube.png";
    import poweredByWorkAdventureImg from "../images/Powered_By_WorkAdventure_Big.png";
    import pwaDefaultBackground from "../images/pwa-background-image.jpg";
    import { gameManager } from "../../Phaser/Game/GameManager";
    import { analyticsClient } from "../../Administration/AnalyticsClient";
    import {
        pwaInstallUiStore,
        initPwaInstallUiListeners,
        installPwaFromStore,
        continuePwaInBrowser,
        neverShowPwaPage,
    } from "../../Stores/PwaInstallStore";
    import { detectIos, markPwaPromptNeverShow } from "../../Utils/PwaInstallEligibility";
    import InputSwitch from "../Input/InputSwitch.svelte";
    import { IconApps, IconAppWindow, IconHistory } from "@wa-icons";

    let logo = $state(logoImg);
    let sceneBg = $state("/static/images/map-bg.svg");

    let neverShowAgain = $state(false);
    let unsubscribePwa: (() => void) | undefined;

    onMount(() => {
        unsubscribePwa = initPwaInstallUiListeners();
        analyticsClient.pwaInstallPromptShown(detectIos());

        gameManager.currentStartedRoomPromise
            .then((room) => {
                if (room.loginSceneLogo) {
                    logo = room.loginSceneLogo;
                }
                if (room.backgroundSceneImage) {
                    sceneBg = room.backgroundSceneImage;
                }
            })
            .catch((e) => {
                console.error("Error while loading current room for PWA install screen", e);
            });
    });

    onDestroy(() => {
        unsubscribePwa?.();
    });

    function handleContinue(): void {
        if (neverShowAgain) {
            neverShowPwaPage();
            return;
        }
        continuePwaInBrowser();
    }

    async function handleInstall(): Promise<void> {
        if (neverShowAgain) {
            markPwaPromptNeverShow();
        }
        await installPwaFromStore();
    }
</script>

<div
    class="pwa-install-screen loginScene relative z-30 flex w-full h-full flex-col items-center justify-center overflow-y-auto pointer-events-auto"
>
    <!-- Full-page background -->
    <div
        class="absolute inset-0 z-0 h-full w-full bg-no-repeat bg-contain bg-center bg-[#F1F0EB]"
        style="background-image: url('{sceneBg}');"
        aria-hidden="true"
    ></div>
    <!-- Dim overlay (room tint or default blue) -->
    <div class="absolute inset-0 z-1 h-full w-full backdrop-blur-sm" aria-hidden="true"></div>

    <div
        class="relative z-10 mx-auto flex w-full max-w-[min(424px,calc(100vw-2rem))] flex-col items-center gap-6 py-10"
    >
        <div class="w-full rounded-md2 bg-mlight text-left shadow-light">
            <!-- Header: banner + logo + pitch -->
            <div class="hidden relative h-[200px] overflow-hidden rounded-t-md2 md:block">
                <div class="absolute inset-0" aria-hidden="true">
                    <img draggable="false" src={pwaDefaultBackground} alt="" class="h-full w-full object-cover" />
                </div>
                <div class="absolute inset-0 bg-black/40" aria-hidden="true"></div>
                <div class="absolute inset-0 flex flex-col items-center justify-center gap-4 px-6 py-4 text-center">
                    <img
                        draggable="false"
                        src={logo}
                        alt=""
                        class="h-auto w-[min(230px,75vw)] object-contain {gameManager.currentStartedRoom.loginSceneLogo
                            ? 'max-h-[100px]'
                            : 'max-h-[35px] brightness-0 invert'}"
                    />
                    <p class="m-0 w-full text-sm font-medium leading-[19px] text-white">
                        {#if $pwaInstallUiStore.isIos}
                            {$LL.warning.pwaInstall.descriptionIos()}
                        {:else}
                            {$LL.warning.pwaInstall.description()}
                        {/if}
                    </p>
                </div>
            </div>

            <div class="flex flex-col gap-6 px-8 py-9">
                <ul class="m-0 flex list-none flex-col gap-2 p-0">
                    <li class="flex gap-3">
                        <span
                            class="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg text-mdark/40"
                            aria-hidden="true"
                        >
                            <IconApps class="h-6 w-6" />
                        </span>
                        <div class="flex min-w-0 flex-col gap-2">
                            <p class="m-0 text-base font-bold leading-6 text-black">
                                {$LL.warning.pwaInstall.feature1Title()}
                            </p>
                            <p class="m-0 text-sm leading-normal text-black/80">
                                {$LL.warning.pwaInstall.feature1Description()}
                            </p>
                        </div>
                    </li>
                    <li class="flex gap-3">
                        <span
                            class="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg text-mdark/40"
                            aria-hidden="true"
                        >
                            <IconAppWindow class="h-6 w-6" />
                        </span>
                        <div class="flex min-w-0 flex-col gap-2">
                            <p class="m-0 text-base font-bold leading-6 text-black">
                                {$LL.warning.pwaInstall.feature2Title()}
                            </p>
                            <p class="m-0 text-sm leading-normal text-black/80">
                                {$LL.warning.pwaInstall.feature2Description()}
                            </p>
                        </div>
                    </li>
                    <li class="flex gap-3">
                        <span
                            class="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg text-mdark/40"
                            aria-hidden="true"
                        >
                            <IconHistory class="h-6 w-6" />
                        </span>
                        <div class="flex min-w-0 flex-col gap-2">
                            <p class="m-0 text-base font-bold leading-6 text-black">
                                {$LL.warning.pwaInstall.feature3Title()}
                            </p>
                            <p class="m-0 text-sm leading-normal text-black/80">
                                {$LL.warning.pwaInstall.feature3Description()}
                            </p>
                        </div>
                    </li>
                </ul>

                {#if $pwaInstallUiStore.isIos}
                    <div class="rounded-lg bg-white/60 p-4 text-sm text-mdark ring-1 ring-mblue-lighter">
                        <p class="m-0 font-semibold">{$LL.warning.pwaInstall.iosStepsTitle()}</p>
                        <ol class="m-0 list-decimal space-y-1.5 pl-5">
                            <li>{$LL.warning.pwaInstall.iosStep1()}</li>
                            <li>{$LL.warning.pwaInstall.iosStep2()}</li>
                            <li>{$LL.warning.pwaInstall.iosStep3()}</li>
                        </ol>
                    </div>
                {/if}

                <div class="flex flex-col gap-6">
                    <div class="flex flex-col gap-2">
                        {#if $pwaInstallUiStore.deferredPrompt && !$pwaInstallUiStore.isIos}
                            <button
                                type="button"
                                class="btn btn-secondary w-full rounded-2xl text-lg whitespace-normal"
                                onclick={handleInstall}
                                disabled={$pwaInstallUiStore.installing}
                                data-testid="pwa-install-button"
                            >
                                <svg
                                    class="h-5 w-5 shrink-0 mr-1"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                >
                                    <path
                                        stroke-linecap="round"
                                        stroke-linejoin="round"
                                        stroke-width="2"
                                        d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
                                    />
                                </svg>
                                {$pwaInstallUiStore.installing
                                    ? $LL.warning.pwaInstall.installing()
                                    : $LL.warning.pwaInstall.install()}
                            </button>
                        {/if}

                        <button
                            type="button"
                            class="btn btn-border btn-secondary w-full rounded-2xl text-sm"
                            onclick={() => {
                                analyticsClient.pwaContinueInBrowserClick();
                                handleContinue();
                            }}
                            data-testid="pwa-install-skip"
                        >
                            {$LL.warning.pwaInstall.continue()}
                        </button>
                    </div>

                    <div class="flex justify-center">
                        <InputSwitch
                            id="pwa-install-never-show-input"
                            bind:value={neverShowAgain}
                            margin=""
                            alignLabel="center"
                        >
                            <span class="text-sm font-medium leading-[1.2] text-mdark">
                                {$LL.warning.pwaInstall.neverShowPage()}
                            </span>
                        </InputSwitch>
                    </div>
                </div>
            </div>
        </div>

        {#if logo !== logoImg && gameManager.currentStartedRoom.showPoweredBy !== false}
            <section class="flex justify-center pb-2">
                <img draggable="false" src={poweredByWorkAdventureImg} alt="Powered by WorkAdventure" class="h-14" />
            </section>
        {/if}
    </div>
</div>
